import { DEG, hash32, Rng, type Vec3 } from '../math';
import { HIT_ZONES, type HitZone } from './character';
import { rayAabb } from '../voxel/raycast';

/**
 * Shot seeds carry the spread used at fire time in the top 8 bits (tenths of a degree) so
 * clients can reproduce pellet directions for tracers without extra bandwidth.
 */
export function packShotSeed(spreadDeg: number, rand24: number): number {
  const q = Math.min(255, Math.round(spreadDeg * 10));
  return ((q << 24) | (rand24 & 0xffffff)) >>> 0;
}

export function shotSeedSpread(seed: number): number {
  return (seed >>> 24) / 10;
}

export function shotSeed(playerId: number, counter: number, spreadDeg: number): number {
  return packShotSeed(spreadDeg, hash32(playerId, counter, 0x5eed));
}

/**
 * Direction of pellet `pellet` for a shot. Uniform in a cone of half-angle spread.
 * Yaw 0 looks toward -Z, positive pitch looks up.
 */
export function pelletDirection(yaw: number, pitch: number, seed: number, pellet: number, out: Vec3): Vec3 {
  const spread = shotSeedSpread(seed) * DEG;
  let dy = 0;
  let dp = 0;
  if (spread > 0) {
    const rng = new Rng(hash32(seed, pellet, 0x9e11));
    const r = Math.sqrt(rng.next()) * spread;
    const a = rng.next() * Math.PI * 2;
    dy = Math.cos(a) * r;
    dp = Math.sin(a) * r;
  }
  const y = yaw + dy / Math.max(0.2, Math.cos(pitch));
  const p = pitch + dp;
  const cp = Math.cos(p);
  out.x = -Math.sin(y) * cp;
  out.y = Math.sin(p);
  out.z = -Math.cos(y) * cp;
  return out;
}

export interface ZoneHit {
  zone: HitZone;
  dist: number;
  /** Hit point in the target's local frame (meters). */
  lx: number;
  ly: number;
  lz: number;
}

/**
 * Ray vs a player's fixed hit zones. The target stands at (px, py, pz) facing `yaw`.
 * `pad` inflates boxes (melee). Returns the nearest zone hit or null.
 */
export function rayVsPlayer(
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  maxDist: number,
  px: number,
  py: number,
  pz: number,
  yaw: number,
  pad = 0,
): ZoneHit | null {
  // Transform the ray into the target's local frame (inverse yaw rotation).
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  const rx = ox - px;
  const ry = oy - py;
  const rz = oz - pz;
  const lox = rx * c - rz * s;
  const loz = rx * s + rz * c;
  const ldx = dx * c - dz * s;
  const ldz = dx * s + dz * c;
  let best: ZoneHit | null = null;
  for (const zb of HIT_ZONES) {
    const t = rayAabb(
      lox, ry, loz, ldx, dy, ldz,
      zb.min[0] - pad, zb.min[1] - pad, zb.min[2] - pad,
      zb.max[0] + pad, zb.max[1] + pad, zb.max[2] + pad,
      maxDist,
    );
    if (t < 0) continue;
    if (!best || t < best.dist) {
      best = { zone: zb.zone, dist: t, lx: lox + ldx * t, ly: ry + dy * t, lz: loz + ldz * t };
    }
  }
  return best;
}

/** Damage multiplier for distance falloff. */
export function falloffMultiplier(dist: number, start?: number, end?: number, min?: number): number {
  if (start === undefined || end === undefined || min === undefined || dist <= start) return 1;
  if (dist >= end) return min;
  return 1 - ((dist - start) / (end - start)) * (1 - min);
}
